import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { eventService } from '@/services/event.service';
import type {
  EventWithDetails,
  EventFilters,
  CreateEventInput,
  UpdateEventInput,
  ParticipantStatus,
  ParticipantWithUser,
  EventComment,
  PaginatedEventsResponse,
} from '@/services/event.service';
import { ApiError } from '@/services/api';

// Event state interface
interface EventState {
  events: EventWithDetails[];
  currentEvent: EventWithDetails | null;
  comments: EventComment[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  commentsMeta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  filters: EventFilters;
  isLoading: boolean;
  isLoadingEvent: boolean;
  isLoadingComments: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isUpdatingRsvp: boolean;
  isAddingComment: boolean;
  error: string | null;
}

// Initial state
const initialState: EventState = {
  events: [],
  currentEvent: null,
  comments: [],
  meta: null,
  commentsMeta: null,
  filters: {
    page: 1,
    limit: 10,
    sortBy: 'startTime',
    sortOrder: 'asc',
  },
  isLoading: false,
  isLoadingEvent: false,
  isLoadingComments: false,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
  isUpdatingRsvp: false,
  isAddingComment: false,
  error: null,
};

// Async thunks

// Fetch events list
export const fetchEvents = createAsyncThunk<
  PaginatedEventsResponse,
  EventFilters | undefined,
  { rejectValue: string }
>('events/fetchEvents', async (filters, { rejectWithValue, getState }) => {
  try {
    const state = getState() as { events: EventState };
    const mergedFilters = { ...state.events.filters, ...filters };
    return await eventService.getEvents(mergedFilters);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Events');
  }
});

// Fetch single event
export const fetchEventById = createAsyncThunk<
  EventWithDetails,
  string,
  { rejectValue: string }
>('events/fetchEventById', async (id, { rejectWithValue }) => {
  try {
    return await eventService.getEventById(id);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden des Events');
  }
});

// Create event
export const createEvent = createAsyncThunk<
  EventWithDetails,
  CreateEventInput,
  { rejectValue: string }
>('events/createEvent', async (data, { rejectWithValue }) => {
  try {
    return await eventService.createEvent(data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Erstellen des Events');
  }
});

// Update event
export const updateEvent = createAsyncThunk<
  EventWithDetails,
  { id: string; data: UpdateEventInput },
  { rejectValue: string }
>('events/updateEvent', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await eventService.updateEvent(id, data);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des Events');
  }
});

// Delete event
export const deleteEvent = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('events/deleteEvent', async (id, { rejectWithValue }) => {
  try {
    await eventService.deleteEvent(id);
    return id;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Events');
  }
});

// Update RSVP status
export const updateRsvpStatus = createAsyncThunk<
  { eventId: string; participant: ParticipantWithUser },
  { eventId: string; userId: string; status: ParticipantStatus },
  { rejectValue: string }
>('events/updateRsvpStatus', async ({ eventId, userId, status }, { rejectWithValue }) => {
  try {
    const participant = await eventService.updateRsvpStatus(eventId, userId, { status });
    return { eventId, participant };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Aktualisieren des RSVP-Status');
  }
});

// Fetch comments
export const fetchEventComments = createAsyncThunk<
  { comments: EventComment[]; meta: EventState['commentsMeta'] },
  { eventId: string; page?: number; limit?: number },
  { rejectValue: string }
>('events/fetchComments', async ({ eventId, page = 1, limit = 20 }, { rejectWithValue }) => {
  try {
    const response = await eventService.getComments(eventId, page, limit);
    return { comments: response.comments, meta: response.meta };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Kommentare');
  }
});

// Add comment
export const addEventComment = createAsyncThunk<
  EventComment,
  { eventId: string; content: string },
  { rejectValue: string }
>('events/addComment', async ({ eventId, content }, { rejectWithValue }) => {
  try {
    return await eventService.addComment(eventId, { comment: content });
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Hinzufuegen des Kommentars');
  }
});

// Delete comment
export const deleteEventComment = createAsyncThunk<
  string,
  { eventId: string; commentId: string },
  { rejectValue: string }
>('events/deleteComment', async ({ eventId, commentId }, { rejectWithValue }) => {
  try {
    await eventService.deleteComment(eventId, commentId);
    return commentId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Loeschen des Kommentars');
  }
});

// Event slice
const eventSlice = createSlice({
  name: 'events',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentEvent: (state) => {
      state.currentEvent = null;
      state.comments = [];
      state.commentsMeta = null;
    },
    setFilters: (state, action: PayloadAction<Partial<EventFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        sortBy: 'startTime',
        sortOrder: 'asc',
      };
    },
    resetEvents: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch events
    builder
      .addCase(fetchEvents.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        state.isLoading = false;
        state.events = action.payload.events;
        state.meta = action.payload.meta;
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch single event
    builder
      .addCase(fetchEventById.pending, (state) => {
        state.isLoadingEvent = true;
        state.error = null;
      })
      .addCase(fetchEventById.fulfilled, (state, action) => {
        state.isLoadingEvent = false;
        state.currentEvent = action.payload;
      })
      .addCase(fetchEventById.rejected, (state, action) => {
        state.isLoadingEvent = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Create event
    builder
      .addCase(createEvent.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createEvent.fulfilled, (state, action) => {
        state.isCreating = false;
        state.events.unshift(action.payload);
      })
      .addCase(createEvent.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload ?? 'Fehler beim Erstellen';
      });

    // Update event
    builder
      .addCase(updateEvent.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.events.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.events[index] = action.payload;
        }
        if (state.currentEvent?.id === action.payload.id) {
          state.currentEvent = action.payload;
        }
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload ?? 'Fehler beim Aktualisieren';
      });

    // Delete event
    builder
      .addCase(deleteEvent.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.events = state.events.filter((e) => e.id !== action.payload);
        if (state.currentEvent?.id === action.payload) {
          state.currentEvent = null;
        }
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });

    // Update RSVP
    builder
      .addCase(updateRsvpStatus.pending, (state) => {
        state.isUpdatingRsvp = true;
        state.error = null;
      })
      .addCase(updateRsvpStatus.fulfilled, (state, action) => {
        state.isUpdatingRsvp = false;
        const { eventId, participant } = action.payload;

        // Update in events list
        const eventIndex = state.events.findIndex((e) => e.id === eventId);
        if (eventIndex !== -1) {
          const participantIndex = state.events[eventIndex].participants.findIndex(
            (p) => p.userId === participant.userId
          );
          if (participantIndex !== -1) {
            // Update existing participant
            state.events[eventIndex].participants[participantIndex] = participant;
          } else {
            // Add new participant
            state.events[eventIndex].participants.push(participant);
          }
        }

        // Update in current event
        if (state.currentEvent?.id === eventId) {
          const participantIndex = state.currentEvent.participants.findIndex(
            (p) => p.userId === participant.userId
          );
          if (participantIndex !== -1) {
            // Update existing participant
            state.currentEvent.participants[participantIndex] = participant;
          } else {
            // Add new participant
            state.currentEvent.participants.push(participant);
          }
        }
      })
      .addCase(updateRsvpStatus.rejected, (state, action) => {
        state.isUpdatingRsvp = false;
        state.error = action.payload ?? 'Fehler beim RSVP-Update';
      });

    // Fetch comments
    builder
      .addCase(fetchEventComments.pending, (state) => {
        state.isLoadingComments = true;
        state.error = null;
      })
      .addCase(fetchEventComments.fulfilled, (state, action) => {
        state.isLoadingComments = false;
        state.comments = action.payload.comments;
        state.commentsMeta = action.payload.meta;
      })
      .addCase(fetchEventComments.rejected, (state, action) => {
        state.isLoadingComments = false;
        state.error = action.payload ?? 'Fehler beim Laden der Kommentare';
      });

    // Add comment
    builder
      .addCase(addEventComment.pending, (state) => {
        state.isAddingComment = true;
        state.error = null;
      })
      .addCase(addEventComment.fulfilled, (state, action) => {
        state.isAddingComment = false;
        state.comments.unshift(action.payload);
        if (state.currentEvent) {
          state.currentEvent._count.comments += 1;
        }
      })
      .addCase(addEventComment.rejected, (state, action) => {
        state.isAddingComment = false;
        state.error = action.payload ?? 'Fehler beim Hinzufuegen';
      });

    // Delete comment
    builder
      .addCase(deleteEventComment.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteEventComment.fulfilled, (state, action) => {
        state.comments = state.comments.filter((c) => c.id !== action.payload);
        if (state.currentEvent && state.currentEvent._count.comments > 0) {
          state.currentEvent._count.comments -= 1;
        }
      })
      .addCase(deleteEventComment.rejected, (state, action) => {
        state.error = action.payload ?? 'Fehler beim Loeschen';
      });
  },
});

// Export actions
export const {
  clearError,
  clearCurrentEvent,
  setFilters,
  resetFilters,
  resetEvents,
} = eventSlice.actions;

// Selectors
export const selectEvents = (state: { events: EventState }) => state.events.events;
export const selectCurrentEvent = (state: { events: EventState }) => state.events.currentEvent;
export const selectEventComments = (state: { events: EventState }) => state.events.comments;
export const selectEventsMeta = (state: { events: EventState }) => state.events.meta;
export const selectCommentsMeta = (state: { events: EventState }) => state.events.commentsMeta;
export const selectEventsFilters = (state: { events: EventState }) => state.events.filters;
export const selectEventsLoading = (state: { events: EventState }) => ({
  isLoading: state.events.isLoading,
  isLoadingEvent: state.events.isLoadingEvent,
  isLoadingComments: state.events.isLoadingComments,
  isCreating: state.events.isCreating,
  isUpdating: state.events.isUpdating,
  isDeleting: state.events.isDeleting,
  isUpdatingRsvp: state.events.isUpdatingRsvp,
  isAddingComment: state.events.isAddingComment,
});
export const selectEventsError = (state: { events: EventState }) => state.events.error;

// Export reducer
export default eventSlice.reducer;
